import {
  ChangeDetectorRef,
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { debounceTime, startWith, tap } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { TellentGroupKind, TellentMemberManagedBy } from '@core/tellent-types';
import {
  AvatarComponent,
  type AvatarShape,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  IconName,
  InputComponent,
  RtTooltip,
  SpinnerComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { SelectGroupsController } from './select-groups.controller';
import { SelectGroupsService } from './select-groups.service';
import { GROUP_KIND } from '../edit-member-admin-dialog/edit-member-groups-tab/edit-member-group-tab.controller';

type SelectGroupsDialogData = {
  saveAction: (groupsGuids: string[]) => Promise<{ isSuccess: boolean }>;
  groupsIds: string[];
  memberName: string;
};

@Component({
  selector: 'tlc-select-groups-dialog',
  imports: [
    BoxComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    SpinnerComponent,
    VirtualScrollerModule,
    AvatarComponent,
    CheckboxComponent,
    TruncateComponent,
    ButtonLoadingDirective,
    RtTooltip,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './select-groups-dialog.component.html',
  styleUrl: './select-groups-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [SelectGroupsController, SelectGroupsService],
})
export class SelectGroupsDialogComponent implements DialogComponentI<SelectGroupsDialogData, void> {
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly dialogRef = inject(DialogRef);
  protected readonly kind = inject(GROUP_KIND);
  protected readonly TellentMemberManagedBy = TellentMemberManagedBy;
  protected readonly vm = inject(SelectGroupsController);
  protected readonly cdRef = inject(ChangeDetectorRef);
  protected readonly queryControl = new FormControl('', { nonNullable: true });
  protected readonly icons: Record<TellentGroupKind, { name: IconName; shape: AvatarShape }> = {
    [TellentGroupKind.Group]: {
      name: 'account-group',
      shape: 'oval',
    },
    [TellentGroupKind.Department]: {
      name: 'departments-hierarchy-tree',
      shape: 'square',
    },
  };
  protected readonly translationKeys = {
    [TellentGroupKind.Group]: {
      header: 'tlc.select_group_dialog.header',
      subheader: 'tlc.select_group_dialog.subheader',
      searchPlaceholder: 'tlc.select_group_dialog.search_placeholder',
      alreadyAdded: 'tlc.select_group_dialog.already_added',
      managedByOkta: 'tlc.select_group_dialog.managed_by_okta',
      managedByOther: 'tlc.select_group_dialog.managed_by_other',
      groupMembers: 'tlc.select_group_dialog.group_members',
      emptyState: 'tlc.select_group_dialog.search_empty_state',
      clearSearch: 'tlc.select_group_dialog.clear_search',
      addGroups: 'tlc.select_group_dialog.add_groups',
    },
    [TellentGroupKind.Department]: {
      header: 'tlc.select_department_dialog.header',
      subheader: 'tlc.select_department_dialog.subheader',
      searchPlaceholder: 'tlc.select_department_dialog.search_placeholder',
      alreadyAdded: 'tlc.select_department_dialog.already_added',
      managedByOkta: 'tlc.select_department_dialog.managed_by_okta',
      managedByOther: 'tlc.select_department_dialog.managed_by_other',
      groupMembers: 'tlc.select_department_dialog.department_members',
      emptyState: 'tlc.select_department_dialog.search_empty_state',
      clearSearch: 'tlc.select_department_dialog.clear_search',
      addGroups: 'tlc.select_department_dialog.add_departments',
    },
  };
  protected disabledIds = signal<Record<string, boolean>>({});
  protected saveLoading = signal(false);
  protected memberName: string;

  private saveAction: (membersGuids: string[]) => Promise<{ isSuccess: boolean }>;

  constructor() {
    this.queryControl.valueChanges
      .pipe(
        tap(() => this.vm.isLoading.set(true)),
        debounceTime(300),
        startWith(''),
        this.untilDestroyed,
      )
      .subscribe(value => {
        this.vm.setQuery(value);
      });
  }

  public setDialogData(data: SelectGroupsDialogData): void {
    this.saveAction = data.saveAction;
    this.memberName = data.memberName;
    this.disabledIds.set(data.groupsIds.reduce((acc, guid) => ({ ...acc, [guid]: true }), {}));
  }

  protected async submit() {
    this.saveLoading.set(true);
    const result = await this.saveAction(Array.from(this.vm.selectedIds));
    if (result.isSuccess) {
      this.saveLoading.set(false);
      this.closeDialog();
    }
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }
}
